import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';

import { cn } from '@/lib/utils';
import { UserRole } from '@/state/store/auth/index.type';
import {
  BarChart3,
  Check,
  FileText,
  LayoutDashboard,
  Loader2,
  Mail,
  Settings,
  ShieldCheck,
  UserCog,
  UserPlus,
  Users,
  X,
} from 'lucide-react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';

interface InviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (email: string, firstName: string, lastName: string, role: UserRole) => Promise<void>;
}

interface Permission {
  name: string;
  icon: React.ReactNode;
  admin: boolean;
  editor: boolean;
}

export function InviteDialog({ open, onOpenChange, onConfirm }: InviteDialogProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>('sys_editor');
  const [isInviting, setIsInviting] = useState(false);
  const { t } = useTranslation();
  const { toast } = useToast();

  const permissions: Permission[] = [
    {
      name: t('ADMIN_USERS_INVITEDIALOG_PERMISSIONS_DASHBOARD'),
      icon: <LayoutDashboard className="size-3.5" />,
      admin: true,
      editor: false,
    },
    {
      name: t('ADMIN_USERS_INVITEDIALOG_PERMISSIONS_TEMPLATES'),
      icon: <FileText className="size-3.5" />,
      admin: true,
      editor: true,
    },
    {
      name: t('ADMIN_USERS_INVITEDIALOG_PERMISSIONS_ANALYTICS'),
      icon: <BarChart3 className="size-3.5" />,
      admin: true,
      editor: true,
    },
    {
      name: t('ADMIN_USERS_INVITEDIALOG_PERMISSIONS_SETTINGS'),
      icon: <Settings className="size-3.5" />,
      admin: true,
      editor: false,
    },
    {
      name: t('ADMIN_USERS_INVITEDIALOG_PERMISSIONS_USERS'),
      icon: <Users className="size-3.5" />,
      admin: true,
      editor: false,
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: t('ADMIN_USERS_INVITEDIALOG_ERRORS_INVALIDEMAIL'),
      });
      return;
    }

    if (!firstName.trim() || !lastName.trim()) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: t('ADMIN_USERS_INVITEDIALOG_ERRORS_NAMEREQUIRED'),
      });
      return;
    }

    setIsInviting(true);
    try {
      await onConfirm(email, firstName.trim(), lastName.trim(), role);
      setEmail('');
      setFirstName('');
      setLastName('');
      setRole('sys_admin');
      onOpenChange(false);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || t('ADMIN_USERS_INVITEDIALOG_ERRORS_SENDFAILED'),
      });
    } finally {
      setIsInviting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isInviting) {
      if (!newOpen) {
        setEmail('');
        setFirstName('');
        setLastName('');
        setRole('sys_editor');
      }
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-lg max-h-[90dvh] overflow-y-auto p-4 sm:p-6 rounded-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader className="pr-8">
            <div className="flex items-center gap-3">
              <div className="flex size-9 sm:size-10 shrink-0 items-center justify-center rounded-full bg-selise-blue/10">
                <UserPlus className="size-4 sm:size-5 text-selise-blue" />
              </div>
              <DialogTitle className="text-base sm:text-lg">
                {t('ADMIN_USERS_INVITEDIALOG_TITLE')}
              </DialogTitle>
            </div>
            <DialogDescription className="pt-2 text-xs sm:text-sm">
              {t('ADMIN_USERS_INVITEDIALOG_DESCRIPTION')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 sm:space-y-5 py-3 sm:py-4">
            {/* Email Input */}
            <div className="space-y-2">
              <Label htmlFor="email">{t('ADMIN_USERS_INVITEDIALOG_EMAILLABEL')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-globe-grey" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('ADMIN_USERS_INVITEDIALOG_EMAILPLACEHOLDER')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  disabled={isInviting}
                />
              </div>
            </div>

            {/* Name Inputs */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">{t('ADMIN_USERS_INVITEDIALOG_FIRSTNAMELABEL')}</Label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder={t('ADMIN_USERS_INVITEDIALOG_FIRSTNAMEPLACEHOLDER')}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isInviting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">{t('ADMIN_USERS_INVITEDIALOG_LASTNAMELABEL')}</Label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder={t('ADMIN_USERS_INVITEDIALOG_LASTNAMEPLACEHOLDER')}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isInviting}
                />
              </div>
            </div>

            {/* Role Selection Cards */}
            <div className="space-y-2">
              <Label>{t('ADMIN_USERS_INVITEDIALOG_ROLELABEL')}</Label>
              <div className="grid gap-3 sm:grid-cols-2">
                {/* Editor Card */}
                <button
                  type="button"
                  onClick={() => setRole('sys_editor')}
                  disabled={isInviting}
                  className={cn(
                    'flex flex-col items-start rounded-lg border-2 p-3 sm:p-4 text-left transition-all hover:shadow-sm',
                    role === 'sys_editor'
                      ? 'border-lime-green bg-lime-green/5'
                      : 'border-border hover:border-[hsl(var(--lime-green))]/50'
                  )}
                >
                  <div className="flex w-full items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="flex size-8 items-center justify-center rounded-lg bg-lime-green/10">
                        <UserCog className="size-4 text-lime-green" />
                      </div>
                      <span className="font-semibold text-[hsl(var(--fg))]">
                        {t('ADMIN_USERS_ROLES_EDITOR')}
                      </span>
                    </div>
                    {role === 'sys_editor' && (
                      <div className="flex size-5 items-center justify-center rounded-full bg-lime-green">
                        <Check className="size-3 text-white" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-globe-grey mb-3">
                    {t('ADMIN_USERS_INVITEDIALOG_ROLES_EDITORDESCRIPTION')}
                  </p>
                  <div className="space-y-1.5 w-full">
                    {permissions.map((perm) => (
                      <div key={perm.name} className="flex items-center gap-2 text-xs">
                        {perm.editor ? (
                          <Check className="size-3 text-lime-green" />
                        ) : (
                          <X className="size-3 text-globe-grey/50" />
                        )}
                        <span
                          className={perm.editor ? 'text-[hsl(var(--fg))]' : 'text-globe-grey/50'}
                        >
                          {perm.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </button>

                {/* Admin Card */}
                <button
                  type="button"
                  onClick={() => setRole('sys_admin')}
                  disabled={isInviting}
                  className={cn(
                    'flex flex-col items-start rounded-lg border-2 p-3 sm:p-4 text-left transition-all hover:shadow-sm',
                    role === 'sys_admin'
                      ? 'border-selise-blue bg-selise-blue/5'
                      : 'border-border hover:border-selise-blue/50'
                  )}
                >
                  <div className="flex w-full items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="flex size-8 items-center justify-center rounded-lg bg-selise-blue/10">
                        <ShieldCheck className="size-4 text-selise-blue" />
                      </div>
                      <span className="font-semibold text-[hsl(var(--fg))]">
                        {t('ADMIN_USERS_ROLES_ADMIN')}
                      </span>
                    </div>
                    {role === 'sys_admin' && (
                      <div className="flex size-5 items-center justify-center rounded-full bg-selise-blue">
                        <Check className="size-3 text-white" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-globe-grey mb-3">
                    {t('ADMIN_USERS_INVITEDIALOG_ROLES_ADMINDESCRIPTION')}
                  </p>
                  <div className="space-y-1.5 w-full">
                    {permissions.map((perm) => (
                      <div key={perm.name} className="flex items-center gap-2 text-xs">
                        {perm.admin ? (
                          <Check className="size-3 text-lime-green" />
                        ) : (
                          <X className="size-3 text-globe-grey/50" />
                        )}
                        <span
                          className={perm.admin ? 'text-[hsl(var(--fg))]' : 'text-globe-grey/50'}
                        >
                          {perm.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </button>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleOpenChange(false)}
              disabled={isInviting}
              className="w-full sm:w-auto"
            >
              {t('ADMIN_USERS_INVITEDIALOG_ACTIONS_CANCEL')}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isInviting || !email || !firstName.trim() || !lastName.trim()}
              className="w-full sm:w-auto"
            >
              {isInviting && <Loader2 className="size-4 animate-spin mr-2" />}
              {t('ADMIN_USERS_INVITEDIALOG_ACTIONS_SEND')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
